// Brand Profile Page – Manage multiple branding profiles for AI asset generation

import React, { useState, useContext, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import Sidebar from '../components/DashboardSidebar';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

export default function BrandProfile() {
  const { user } = useContext(AuthContext);
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [tagline, setTagline] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [colors, setColors] = useState(['']);
  const [font, setFont] = useState('');
  const [tone, setTone] = useState('Friendly');
  const [notes, setNotes] = useState('');
  const [lang, setLang] = useState(user.language || 'en');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const API_BASE = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    document.title = t('brand_profile');
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/brand-profile/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
      const data = await res.json();
      setProfiles(data);
      if (data.length > 0) loadProfile(data[0]);
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const loadProfile = (profile) => {
    setSelectedProfile(profile._id);
    setCompanyName(profile.companyName || '');
    setTagline(profile.tagline || '');
    setLogoPreview(profile.logoUrl || '');
    setColors(profile.colors || ['']);
    setFont(profile.font || '');
    setTone(profile.tone || 'Friendly');
    setNotes(profile.notes || '');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append('companyName', companyName);
    formData.append('tagline', tagline);
    formData.append('colors', colors.join(', '));
    formData.append('font', font);
    formData.append('tone', tone);
    formData.append('notes', notes);
    if (logoFile) formData.append('logo', logoFile);
    if (selectedProfile) formData.append('profileId', selectedProfile);
    const token = localStorage.getItem('token');

    try {
      const res = await fetch(`${API_BASE}/api/brand-profile/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const result = await res.json();
      toast.success(t('profile_saved'));
      fetchProfiles();
    } catch (error) {
      console.error(error);
      toast.error(t('error_saving_profile'));
    }
  };

  const handleDelete = async () => {
    const confirmed = window.confirm(t('confirm_delete'));
    if (!confirmed) return;
  
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/api/brand-profile/${selectedProfile}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
  
      if (res.ok) {
        setProfiles(prev => prev.filter(p => p._id !== selectedProfile));
        setSelectedProfile(null);
        setCompanyName('');
        setTagline('');
        setLogoPreview('');
        setLogoFile(null);
        setColors(['']);
        setFont('');
        setTone('Friendly');
        setNotes('');
        toast.success(t('profile_deleted'));
      } else {
        toast.error(t('error_saving_profile'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error_saving_profile'));
    }
  };
  

  const handleSetDefault = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/api/brand-profile/set-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ profileId: selectedProfile })
      });
  
      if (res.ok) {
        setProfiles(prev => prev.map(p => ({ ...p, isDefault: p._id === selectedProfile })));
        toast.success(t('set_as_default'));
      } else {
        toast.error(t('error_saving_profile'));
      }
    } catch (error) {
      console.error('Set default error:', error);
      toast.error(t('error_saving_profile'));
    }
  };
  

  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar
          currentLang={lang}
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>
      <div className="flex-1 px-6 py-10">
        <h1 className="text-2xl font-bold mb-6">{t('brand_profile')}</h1>

        {profiles.length > 0 && (
  <div className="mb-6 sm:flex sm:items-end sm:gap-4 space-y-4 sm:space-y-0">
    <div className="sm:w-64">
      <label className="block text-sm font-medium mb-2">{t('select_profile')}</label>
      <select
        value={selectedProfile || ''}
        onChange={(e) => {
          const profile = profiles.find(p => p._id === e.target.value);
          if (profile) loadProfile(profile);
        }}
        className="w-full border rounded p-2"
      >
        {profiles.map(p => (
          <option key={p._id} value={p._id}>
            {p.companyName} {p.isDefault ? `(${t('default')})` : ''}
          </option>
        ))}
      </select>
    </div>

    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={() => {
        setSelectedProfile(null);
        setCompanyName('');
        setTagline('');
        setLogoPreview('');
        setLogoFile(null);
        setColors(['']);
        setFont('');
        setTone('Friendly');
        setNotes('');
        toast.success(t('new_profile_ready'));
      }}>
        {t('new_profile')}
      </Button>

      <Button variant="outline" onClick={handleSetDefault}>{t('set_default')}</Button>
      <Button variant="destructive" onClick={handleDelete}>{t('delete_profile')}</Button>
    </div>
  </div>
)}


        <div className="space-y-4">
          <label className="block text-sm font-medium mb-1">{t('company_name')}</label>
          <Input label={t('company_name')} placeholder={t('company_name')} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
          <label className="block text-sm font-medium mb-1">{t('tagline')}</label>
          <Input label={t('tagline')} placeholder={t('tagline')} value={tagline} onChange={(e) => setTagline(e.target.value)} />

          <label className="block text-sm font-medium mb-1">{t('logo_url')}</label>
          <input type="file" accept="image/*" onChange={handleLogoChange} className="block w-full border p-2 rounded" />
          {logoPreview && <img src={logoPreview} alt="Logo Preview" className="h-20 mt-2 rounded shadow" />}

          <label className="block text-sm font-medium mb-1">{t('brand_colors')}</label>
          <Input label={t('brand_colors')} placeholder="#E91E63, #FFFFFF" value={colors.join(', ')} onChange={(e) => setColors(e.target.value.split(',').map(c => c.trim()))} />
          <label className="block text-sm font-medium mb-1">{t('brand_font')}</label>
          <Input label={t('brand_font')} placeholder={t('optional_font_family')} value={font} onChange={(e) => setFont(e.target.value)} />

          <label className="block text-sm font-medium mb-1">{t('tone')}</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="w-full border rounded p-2">
            <option value="Friendly">{t('friendly')}</option>
            <option value="Professional">{t('professional')}</option>
            <option value="Luxury">{t('luxury')}</option>
            <option value="Witty">{t('witty')}</option>
          </select>

          <label className="block text-sm font-medium mt-4 mb-1">{t('notes')}</label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder={t('notes_placeholder')} />

          <Button onClick={handleSave}>{t('save_profile')}</Button>
        </div>
      </div>
    </div>
  );
}
